import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { useInView } from '@/hooks/use-in-view'

interface CountUpProps {
    value: number
    duration?: number
    className?: string
}

const formatNumber = (value: number): string => {
    return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}

/**
 * Counts up in chunky retro steps once it scrolls into view.
 * Server markup shows the final number, screen readers always get the final number.
 */
const CountUp: FC<CountUpProps> = ({ value, duration = 1400, className }): ReactNode => {
    const [ref, inView] = useInView({ threshold: 0.6 })
    const [display, setDisplay] = useState(value)
    const [armed, setArmed] = useState(false)

    useEffect(() => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        setDisplay(0)
        setArmed(true)
    }, [])

    useEffect(() => {
        if (!armed || !inView) return

        let frame = 0
        let last = 0

        const start = performance.now()

        const tick = (now: number) => {
            const progress = Math.min(1, (now - start) / duration)

            if (now - last > 55 || progress === 1) {
                last = now
                setDisplay(Math.round(value * (1 - Math.pow(1 - progress, 3))))
            }

            if (progress < 1) frame = window.requestAnimationFrame(tick)
        }

        frame = window.requestAnimationFrame(tick)

        return () => window.cancelAnimationFrame(frame)
    }, [armed, inView, value, duration])

    return (
        <span ref={ref} className={className}>
            <span aria-hidden='true'>{formatNumber(display)}</span>
            <span className='sr-only'>{formatNumber(value)}</span>
        </span>
    )
}

export default CountUp
